import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { portfolioData } from '../mock';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const ArtProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = portfolioData.artProjects.find(p => p.slug === slug);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-4xl font-black text-white mb-4">Project Not Found</h1>
          <Link to="/" className="text-cyan-400 hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Find next project
  const currentIndex = portfolioData.artProjects.findIndex(p => p.slug === slug);
  const nextProject = portfolioData.artProjects[(currentIndex + 1) % portfolioData.artProjects.length];

  return (
    <div className="min-h-screen bg-black">
      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-zinc-900">
        <nav className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link 
            to="/" 
            className="text-xl font-black tracking-tighter text-white hover:text-cyan-400 transition-colors"
          >
            SLOOMX
          </Link>
          <Link 
            to="/#art"
            className="group flex items-center gap-2 text-sm text-zinc-400 hover:text-white font-medium transition-colors uppercase tracking-wider"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            All Projects
          </Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 animate-fade-in-up">
            <span className="text-sm tracking-[0.3em] text-cyan-400 uppercase font-medium mb-4 block">
              Art Project · {String(currentIndex + 1).padStart(2, '0')} / {String(portfolioData.artProjects.length).padStart(2, '0')}
            </span>
            <h1 className="text-7xl md:text-9xl font-black text-white tracking-tight uppercase leading-none mb-8">
              {project.title}
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl leading-relaxed">
              {project.description}
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto space-y-6">
          {project.gallery.map((image, index) => (
            <div
              key={index}
              className="overflow-hidden bg-zinc-900"
              style={{
                animation: `fade-in-up 0.8s ease-out ${index * 0.1}s both`
              }}
            >
              <img
                src={image}
                alt={`${project.title} - ${index + 1}`}
                className="w-full h-auto object-contain"
                loading={index < 2 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Next Project CTA */}
      <section className="px-6 pb-32 border-t border-zinc-900 pt-24">
        <div className="max-w-7xl mx-auto">
          <Link 
            to={`/project/${nextProject.slug}`}
            className="group block"
          >
            <span className="text-sm tracking-[0.3em] text-zinc-500 uppercase font-medium mb-4 block">
              Next Project
            </span>
            <div className="flex items-baseline justify-between gap-4 mb-6">
              <h2 className="text-5xl md:text-7xl font-black text-white tracking-tight uppercase group-hover:text-cyan-400 transition-colors">
                {nextProject.title}
              </h2>
              <ArrowRight className="h-12 w-12 text-zinc-500 group-hover:text-cyan-400 group-hover:translate-x-2 transition-all duration-300 flex-shrink-0" />
            </div>
            <div className="aspect-video overflow-hidden bg-zinc-900 max-w-3xl">
              <img
                src={nextProject.image}
                alt={nextProject.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Link 
            to="/"
            className="text-2xl font-black tracking-tighter text-white hover:text-cyan-400 transition-colors"
          >
            SLOOMX
          </Link>
          <p className="text-zinc-600 text-sm mt-4">
            © {new Date().getFullYear()} Sloomx. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ArtProjectDetail;
